import styled from '@emotion/styled';
import React, {
  useState,
  MouseEvent,
  TouchEvent,
  KeyboardEvent,
  MutableRefObject,
  useEffect
} from 'react';
import { CardContainer } from './card-container';
import { MediaContainer } from './media-container';
import { PaginationContainer } from './pagination-container';
import {
  FeaturesHighlightContainerProps,
  HighlightRootProps
} from '../models/features-highlight-props';
import {
  LAYOUT_TYPE,
  SWIPE_DIRECTION,
  defaultAnalyticsData,
  NUM_OF_CARDS,
  MEDIA_TYPE,
  MIN_CYCLE_TIME
} from '../utils/constants';
import { messages } from '../messages';
import { FormattedHTMLMessage } from 'react-intl';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

export function FeaturesHighlightContainer({
  layoutType,
  colorCoding,
  isColorBar,
  barColor,
  isCategoryLabel,
  mediaType,
  mediaSourceLinks,
  intl
}: FeaturesHighlightContainerProps) {
  const [selectedMedia, setSelectedMedia] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragAmount, setDragAmount] = useState(0);
  const [startX, setStartX] = useState(0);
  const [nextAfter, setNextAfter] = useState(0);
  const timeoutRef: MutableRefObject<any | null> = React.useRef(null);
  const selectedRef: MutableRefObject<number> = React.useRef(selectedMedia);

  const isTouchDevice =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  const { createAnalyticsEvent } = useAnalyticsEvents();
  let cardClickAnalyticsData = {
    ...defaultAnalyticsData,
    action: 'clicked',
    eventType: 'UI',
    actionSubject: 'card'
  };

  const cardClickHandler = (
    clickedElement: number,
    mouseClick?: MouseEvent
  ) => {
    mouseClick && mouseClick.preventDefault();
    changeActiveElement(clickedElement);
  };

  const keyPressHandler = (
    clickedElement: number,
    keyPress?: KeyboardEvent
  ) => {
    if (keyPress?.key == ' ' || keyPress?.key == 'Enter') {
      changeActiveElement(clickedElement);
    }
  };

  const changeActiveElement = (clickedElement: number) => {
    if (clickedElement === selectedMedia) {
      return;
    }

    if (clickedElement < 1 || clickedElement > NUM_OF_CARDS) {
      return;
    }

    setSelectedMedia(clickedElement);

    createAnalyticsEvent({
      ...cardClickAnalyticsData,
      cardNumber: clickedElement
    }).fire();
  };

  const startDrag = (touchEvent?: TouchEvent, mouseEvent?: MouseEvent) => {
    if (isTouchDevice && touchEvent) {
      setIsDragging(true);
      setStartX(touchEvent.touches[0].clientX);
    } else if (mouseEvent && window.innerWidth < 767) {
      setIsDragging(true);
      setStartX(mouseEvent.clientX);
    }
  };

  const Drag = (touchEvent?: TouchEvent, mouseEvent?: MouseEvent) => {
    if (isDragging) {
      if (isTouchDevice && touchEvent) {
        setDragAmount(touchEvent.touches[0].clientX - startX);
      } else if (mouseEvent && window.innerWidth < 767) {
        setDragAmount(mouseEvent.clientX - startX);
      }
    }
  };

  const stopDrag = () => {
    if (isDragging) {
      setIsDragging(false);
      // Setting 100 as the amount of distance that needs to be covered in order to trigger a swipe
      if (Math.abs(dragAmount) > 100) {
        if (dragAmount < 0) {
          swipeCards(SWIPE_DIRECTION.LEFT);
        } else {
          swipeCards(SWIPE_DIRECTION.RIGHT);
        }
      }
    }
  };

  const swipeCards = (direction: SWIPE_DIRECTION) => {
    let nextSelected = selectedRef.current;
    if (
      direction === SWIPE_DIRECTION.LEFT &&
      selectedMedia < mediaSourceLinks.length
    ) {
      nextSelected += 1;
    } else if (direction === SWIPE_DIRECTION.RIGHT && selectedMedia > 1) {
      nextSelected -= 1;
    }
    setSelectedMedia(nextSelected);

    createAnalyticsEvent({
      ...cardClickAnalyticsData,
      isTouchDevice: isTouchDevice,
      cardNumber: nextSelected
    }).fire();
  };

  const nextHighlight = () => {
    const nextId = selectedRef.current + 1;
    setSelectedMedia(nextId > NUM_OF_CARDS ? 1 : nextId);
  };

  const videoEnded = () => {
    if (Date.now() >= nextAfter) {
      nextHighlight();
      return;
    }

    // Time remains before minimum wait, delay till then
    timeoutRef.current = setTimeout(() => {
      nextHighlight();
    }, nextAfter - Date.now());
  };

  const changeInView = (isInView: boolean) => {
    if (!isInView) {
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } else {
      startTimeout();
    }
  };

  const startTimeout = () => {
    if (mediaType === MEDIA_TYPE.IMAGE) {
      timeoutRef.current = setTimeout(() => {
        nextHighlight();
      }, MIN_CYCLE_TIME);
    } else {
      setNextAfter(Date.now() + MIN_CYCLE_TIME);
    }
  };

  useEffect(() => {
    // Storing in the ref is required for the setTimeout that is started for images when called from changeInView
    selectedRef.current = selectedMedia;

    // Clear timeout
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    startTimeout();
  }, [selectedMedia]);

  return (
    <FeaturesHighlightRoot typeColor={colorCoding.typeColor}>
      <HeadingContainer>
        <FormattedHTMLMessage {...messages.heading} />
      </HeadingContainer>
      <CoreContentContainer
        onTouchStart={(e) => startDrag(e)}
        onMouseDown={(e) => startDrag(undefined, e)}
        onTouchMove={(e) => Drag(e)}
        onMouseMove={(e) => Drag(undefined, e)}
        onTouchEnd={() => stopDrag()}
        onMouseUp={() => stopDrag()}
      >
        <CardContainer
          selectedMedia={selectedMedia}
          cardClickHandler={cardClickHandler}
          keyPressHandler={keyPressHandler}
          componentOrder={layoutType == LAYOUT_TYPE.CARDS_LEFT ? 1 : 2}
          colorCoding={colorCoding}
          isColorBar={isColorBar}
          barColor={barColor}
          isCategoryLabel={isCategoryLabel}
        />
        <MediaContainer
          selectedMedia={selectedMedia}
          componentOrder={layoutType == LAYOUT_TYPE.CARDS_RIGHT ? 1 : 2}
          mediaType={mediaType}
          mediaSourceLinks={mediaSourceLinks}
          intl={intl}
          inViewChange={(isInView) => changeInView(isInView)}
          videoEnded={() => videoEnded()}
        />
        <PaginationContainer
          selectedMedia={selectedMedia}
          colorCoding={colorCoding}
        />
      </CoreContentContainer>
    </FeaturesHighlightRoot>
  );
}

export const FeaturesHighlightRoot = styled.div<HighlightRootProps>`
  display: flex;
  flex-wrap: wrap;
  flex-basis: 100%;
  max-width: 100%;
  position: relative;
  flex-direction: row;
  min-width: 992px;
  justify-content: center;
  color: ${(props) => props?.typeColor};
  padding-bottom: 64px;

  @media only screen and (max-width: 1023px) {
    min-width: 736px;
    margin-left: 0px;
    margin-right: 0px;
  }

  @media only screen and (max-width: 767px) {
    min-width: 288px;
    padding-bottom: 0;
  }
`;

export const CoreContentContainer = styled.div`
  display: flex;
  max-width: inherit;
  flex-direction: row;
  align-content: center;
  height: fit-content;

  @media only screen and (max-width: 1023px) {
    flex-direction: column;
    justify-content: center;
  }

  @media only screen and (max-width: 767px) {
    overflow: hidden;
    justify-content: flex-start;
    display: block;
  }
`;

export const HeadingContainer = styled.h2`
  text-align: center;
  flex-basis: calc(99.9999% / 12 * 8 - 16px);
  max-width: calc(99.9999% / 12 * 8 - 16px);
  position: inherit;
  font-size: 36px;
  font-weight: 500;
  line-height: 44px;
  font-style: normal;
  margin-top: 64px;
  margin-bottom: 32px;

  @media only screen and (max-width: 1023px) {
    margin-top: 32px;
  }

  @media only screen and (max-width: 767px) {
    font-size: 24px;
    line-height: 32px;
    flex-basis: 100%;
    max-width: 100%;
  }
`;
